import { Button, Input, Spinner } from "src/components";
import { Fragment, useEffect, useMemo, useState, useCallback } from "react";
import { fetchEinvoicesData } from "src/services/invoice-api";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "src/context/store";
import { invoiceActions } from "src/context/invoice-slice";
import EInvoiceCard from "./E-InvoiceCards/E-InvoiceCard";
import { eInvoiceCardDetailsInfo } from "src/pages/invoice-master/invoice-helper";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast } from "src/hooks/useToast";
import filterIcon from "src/assets/filterIcon.svg";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select.component";
import { Transition } from "@headlessui/react";
import EInvoiceFiltersComponent from "../../container/EInvoiceFilters";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { getAllCompanyDetails } from "src/services/company-api";
import { getCityDetails, getDistrictDetails, getStateDetails } from "src/services/region-api";
import { queryDivisionDetails } from "src/services/division-api";

interface EInvoiceListProps {
  status: string;
  checkDataType: boolean;
  index?: number;
}

const searchOptions = [
  { label: "FUID No", value: "fuid_no" },
  { label: "Invoice Number", value: "invoice_number" },
  { label: "Counter Code", value: "counter_code" },
  { label: "Stockist Medvol Code", value: "stockist_medvol_code" },
  { label: "Company Stockist Code", value: "company_stockist_code" },
];

const EInvoiceList = ({ checkDataType }: EInvoiceListProps) => {
  const { einvoicesData, EInvoiceFilters, EInvoiceFiltersData } = useSelector((state: RootState) => state.invoice);
  const dispatch = useDispatch();
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const [searchCategory, setSearchCategory] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const ref = useOutsideClick(() => setIsFilterOpen(false));

  // Memoized searchParams to avoid unnecessary recalculations
  const searchParams = useMemo(
    () => ({
      company_code: EInvoiceFilters?.company?.[0]?.code,
      division_code: EInvoiceFilters?.division?.[0]?.company_divisioncode,
      startDate: EInvoiceFilters?.dateRange?.[0],
      endDate: EInvoiceFilters?.dateRange?.[1],
      assignee_id: EInvoiceFilters?.assignee?.[0]?.invoice_assignment_id,
      city: EInvoiceFilters?.city?.[0]?.city_name?.split("|")?.[0]?.trim(),
      district: EInvoiceFilters?.district?.[0]?.district_name,
      state: EInvoiceFilters?.state?.[0]?.state_name,
      einvoice_status: EInvoiceFilters?.status?.[0]?.id,
      page_size: 10,
      page_number: 1,
      ...(searchValue && searchCategory ? { [searchCategory]: searchValue } : {}),
    }),
    [EInvoiceFilters, searchCategory, searchValue]
  );

  // Centralized function to fetch e-invoices
  const getEInvoicesData = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await fetchEinvoicesData(searchParams);
      dispatch(invoiceActions.setEinvoicesData(res?.data?.data?.eInvoices));
      setPage(2); // Reset page for new filter/search
      setHasMore(true); // Reset infinite scroll
      toast({ description: "E-invoices loaded successfully", variant: "default" });
    } catch (error: any) {
      console.error("Error fetching data:", error);
      toast({ variant: "destructive", description: "Failed to load e-invoices" });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, searchParams]);

  // Fetch more data for infinite scroll
  const fetchMoreData = async () => {
    if (isLoading) return;
    try {
      const nextPageParams = { ...searchParams, page_number: page };
      const res = await fetchEinvoicesData(nextPageParams);
      const newData = res?.data?.data?.eInvoices;

      if (!newData || newData.length === 0) {
        setHasMore(false);
        return;
      }

      dispatch(invoiceActions.setEinvoicesData([...(einvoicesData || []), ...newData]));
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching more data:", error);
      toast({ variant: "destructive", description: "Failed to load more e-invoices" });
      setHasMore(false);
    }
  };

  // Initial data fetching (on mount) and filter data fetching
  useEffect(() => {
    getEInvoicesData();
  }, [getEInvoicesData]);

  // Fetch filter options (status, company, city, etc.) only once on mount if not already present
  useEffect(() => {
    const fetchInitialFilterData = async () => {
      try {
        if (!EInvoiceFiltersData?.company?.length) {
          const res = await getAllCompanyDetails();
          dispatch(invoiceActions.setEInvoiceFiltersData({ key: "company", value: res?.data }));
        }
        if (!EInvoiceFiltersData?.city?.length) {
          const res = await getCityDetails("");
          dispatch(invoiceActions.setEInvoiceFiltersData({ key: "city", value: res?.data?.data }));
        }
        if (!EInvoiceFiltersData?.district?.length) {
          const res = await getDistrictDetails("");
          dispatch(invoiceActions.setEInvoiceFiltersData({ key: "district", value: res?.data?.data }));
        }
        if (!EInvoiceFiltersData?.state?.length) {
          const res = await getStateDetails("");
          dispatch(invoiceActions.setEInvoiceFiltersData({ key: "state", value: res?.data?.data }));
        }
      } catch (err) {
        console.error("Error fetching filter data:", err);
      }
    };
    fetchInitialFilterData();
  }, [dispatch]); // Only depend on `dispatch`, which is stable

  // Fetch division details when company changes
  useEffect(() => {
    if (EInvoiceFilters?.company?.length && !EInvoiceFiltersData?.division?.length) {
      queryDivisionDetails(`/details/?fields=division_name&company_code=${EInvoiceFilters?.company?.[0]?.code}`)
        .then((res) => dispatch(invoiceActions.setEInvoiceFiltersData({ key: "division", value: res?.data })))
        .catch((err) => console.error("Error fetching divisions:", err));
    } else if (!EInvoiceFilters?.company?.length) {
      dispatch(invoiceActions.setEInvoiceFiltersData({ key: "division", value: [] }));
    }
  }, [dispatch, EInvoiceFilters?.company, EInvoiceFiltersData?.division?.length]);

  if (isLoading && !einvoicesData?.length) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <Transition
        as={Fragment}
        show={isFilterOpen}
        enter="transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <EInvoiceFiltersComponent
            companyDetails={EInvoiceFiltersData?.company || []}
            divisionDetails={EInvoiceFiltersData?.division || []}
            cityDetails={EInvoiceFiltersData?.city || []}
            districtDetails={EInvoiceFiltersData?.district || []}
            stateDetails={EInvoiceFiltersData?.state || []}
            ref={ref}
            onClose={() => setIsFilterOpen(false)}
          />
        </div>
      </Transition>

      <div className="flex gap-2 h-full justify-end">
        <div className="flex items-center w-[400px]">
          <Select value={searchCategory} onValueChange={setSearchCategory}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Search By" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {searchOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Search"
            debounceTime={500}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="w-full px-2 py-1.5 text-gray-700 bg-[#F1F1F1] border-none rounded-l-none rounded-r-md focus:outline-none focus:ring-0"
          />
        </div>
        <div className="flex items-center gap-2 h-full relative">
          {Object.values(EInvoiceFilters).some((value: any) => value?.length > 0) && (
            <span className="absolute top-[-10px] right-[-10px] w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">
              {Object.values(EInvoiceFilters).filter((value: any) => value?.length > 0).length}
            </span>
          )}
          <Button className="bg-[#F1F1F1]" onClick={() => setIsFilterOpen(true)}>
            <img src={filterIcon} alt="filter-icon" />
          </Button>
        </div>
      </div>

      {einvoicesData?.length > 0 ? (
        <div
          id="scrollableDiv"
          className="h-[calc(100vh-250px)] overflow-auto overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
        >
          <InfiniteScroll
            dataLength={einvoicesData.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            }
            scrollableTarget="scrollableDiv"
            endMessage={<div className="py-4 text-center text-gray-500">No more e-invoices to load</div>}
          >
            {einvoicesData.map((item: any) => (
              <EInvoiceCard
                getEInvoicesData={getEInvoicesData}
                key={item?.einvoiceDtls?.[0]?.einvoice_dtl_id}
                item={item}
                cardInfo={eInvoiceCardDetailsInfo(item)}
                checkDataType={checkDataType}
              />
            ))}
          </InfiniteScroll>
        </div>
      ) : (
        <div className="py-4 text-center text-gray-500">No e-invoices available</div>
      )}
    </div>
  );
};

export default EInvoiceList;
